import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_INFIOS_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'
import { CreateAppAPIKeyModal } from './create-app-api-key-modal'

type EnableInfiosAppInput = {
  appType: AppTypeEnum
  password: string
  receiverID: string
  username: string
}

const formSchema = z.object({
  password: z.string().min(1, 'Password is required'),
  receiverID: z.string().min(1, 'Receiver ID is required'),
  username: z.string().min(1, 'Username is required')
})

/**
 * This is the modal for the Infios app.
 */
export function InfiosModal({ isOpen, onClose, appType, app, isModalEditable, setModalEditable }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      password: '',
      receiverID: '',
      username: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableInfiosApp, { loading }] = useMutation<
    { enableInfiosApp: { appEnabled: boolean } },
    EnableInfiosAppInput
  >(ENABLE_INFIOS_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      setFormError('')
      form.reset()
      toast.success('Infios app enabled successfully!')
      setIsApiKeyModalOpen(true)
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
    if (isModalEditable && setModalEditable) {
      setModalEditable(false)
    }
  }

  const handleApiKeyModalClose = () => {
    setIsApiKeyModalOpen(false)
    handleClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableInfiosApp({
      variables: {
        appType: appType as AppTypeEnum,
        password: values.password,
        receiverID: values.receiverID,
        username: values.username
      }
    })
  }

  return (
    <>
      <Dialog open={!!isOpen && !isApiKeyModalOpen && !isModalEditable} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enable Infios for Chain</DialogTitle>
            <DialogDescription>
              Infios integration requires an API username and password. Please request these from your Infios support
              team.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Username</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your Infios API username" {...field} disabled={loading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="receiverID"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Receiver ID</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter receiver ID" {...field} disabled={loading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Enter your Infios API password"
                        {...field}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!!formError && <Alert variant="error" description={formError} />}

              <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
                {!!app?.docs && (
                  <a
                    href={app.docs}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  >
                    See detailed steps here
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}

                <div className="space-x-2">
                  <Button variant="outline" onClick={handleClose} type="button">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                    {loading ? 'Enabling...' : 'Enable'}
                  </Button>
                </div>
              </DialogFooter>
            </div>
          </Form>
        </DialogContent>
      </Dialog>

      <CreateAppAPIKeyModal
        isOpen={!!isOpen && (!!isApiKeyModalOpen || !!isModalEditable)}
        onClose={handleApiKeyModalClose}
        appType={appType}
        app={app}
      />
    </>
  )
}
